<template>
  <main class="grow cmp">
    <!-- HERO -->
    <section class="cmp-hero">
      <div class="cmp-wrap cmp-hero-grid">
        <div class="cmp-hero-copy">
          <h1>{{ page.h1 }}</h1>
          <!-- answerFirst: the "which should you pick?" answer opens the page, where AI answers quote from.
               answerLabel (optional) words the label as the buyer prompt the answer targets. -->
          <div v-if="page.answerFirst && page.directAnswer" class="cmp-answer cmp-answer--first">
            <p class="cmp-answer-label">{{ page.answerLabel || 'Which should you pick?' }}</p>
            <p>{{ page.directAnswer }}</p>
          </div>
          <p class="cmp-lede">{{ page.lede }}</p>
          <div class="cmp-cta-row">
            <!-- On the mobile-proof pages the hero runs the CTA copy test (composables/useCtaTest.ts):
                 both arms go to the Voyager trial; the server always renders the first arm. The
                 min-width fits the longer arm, so the swap after hydration doesn't resize the button. -->
            <a
              :href="signupUrl(heroPlan ? `https://app.workspace369.com/signup?plan=${heroPlan}` : 'https://app.workspace369.com/signup')"
              class="cmp-btn"
              data-conversion-event="start_free_click"
              :data-conversion-label="`${page.h1} primary CTA`"
              :data-conversion-location="page.slug"
              :data-conversion-variant="ctaVariant || undefined"
              :style="ctaEnrolled ? { minWidth: 'min(18em, 100%)' } : null"
            >{{ ctaEnrolled ? ctaLabel : 'Start free trial' }}</a>
            <NuxtLink
              :to="localePath('/pricing')"
              class="cmp-link"
              data-conversion-event="pricing_click"
              :data-conversion-label="`${page.h1} pricing CTA`"
              :data-conversion-location="page.slug"
            >See pricing →</NuxtLink>
          </div>
          <TrialDisclosure :plan="heroPlan" on-light />
          <ChatGptProof :location="page.slug" />
          <!-- The short answer to "which should I pick?" (data: directAnswer), for readers and AI answers. -->
          <div v-if="page.directAnswer && !page.answerFirst" class="cmp-answer">
            <p class="cmp-answer-label">Which should you pick?</p>
            <p>{{ page.directAnswer }}</p>
          </div>
          <p class="cmp-trust">
            Reviewed {{ reviewedDate }} using public product pages, pricing pages, and Workspace369's current product scope.
          </p>
          <CompetitorNotice :competitor="page.competitor" :date="reviewedDate" />
        </div>
        <!-- The real app, as the customer will see it: the Workspace369 client record (real screenshot, 10px frame). -->
        <figure class="cmp-hero-shot">
          <img
            class="cmp-shot-light"
            src="/home/screens/crm-light-clean.webp"
            alt="Workspace369 client record with contact details, invoices and activity in one place"
            width="2732"
            height="1996"
            fetchpriority="high"
          />
          <img
            class="cmp-shot-dark"
            src="/home/screens/crm-dark-clean.webp"
            alt="Workspace369 client record with contact details, invoices and activity in one place"
            width="2732"
            height="1996"
            loading="lazy"
          />
        </figure>
      </div>
    </section>

    <SwitchSave :competitor-slug="page.slug.replace('/compare/', '').replace(/\/$/, '')" :location="page.slug" />

    <!-- VIDEO (only where a comparison video exists: data/comparison-videos.ts) -->
    <ComparisonVideo :path="page.slug" :canonical-url="canonicalUrl" :page-id="`${canonicalUrl}#comparison`" tint />

    <!-- AT A GLANCE -->
    <section class="cmp-section">
      <div class="cmp-wrap">
        <header class="cmp-head">
          <p>At a glance</p>
          <h2>Workspace369 vs {{ page.competitor }}: the short version</h2>
        </header>
        <div class="cmp-shell">
          <table class="cmp-table">
            <thead>
              <tr>
                <th scope="col"></th>
                <th scope="col">{{ page.competitor }}</th>
                <th scope="col" class="cmp-th-us"><Logo class="cmp-logo" /> Workspace369</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in glanceRows" :key="row.label">
                <th scope="row">{{ row.label }}</th>
                <td>
                  <span class="cmp-m-label">{{ page.competitor }}</span>
                  {{ row.competitor }}
                </td>
                <td class="cmp-td-us">
                  <span class="cmp-m-label">Workspace369</span>
                  {{ row.workspace }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- WHY PEOPLE SWITCH (sourced complaint themes + how Workspace369 answers each) -->
    <section v-if="page.switchReasons?.length" class="cmp-section cmp-tint">
      <div class="cmp-wrap">
        <header class="cmp-head">
          <p>Switching</p>
          <h2>{{ page.switchHeading || `Why people switch from ${page.competitor}` }}</h2>
        </header>
        <div class="cmp-why-grid">
          <article v-for="reason in page.switchReasons" :key="reason.theme" class="cmp-why-card">
            <h3>{{ reason.theme }}</h3>
            <p class="cmp-why-evidence">
              {{ reason.evidence }}
              <a :href="reason.sourceUrl" target="_blank" rel="noopener noreferrer" class="cmp-why-src">{{ reason.sourceLabel }} ↗</a>
            </p>
            <p class="cmp-why-answer">
              <span class="cmp-why-label"><Logo class="cmp-logo" /> In Workspace369</span>
              {{ reason.answer }}
            </p>
          </article>
        </div>
        <p class="cmp-note">
          Themes come from {{ page.competitor }}'s own pricing and help pages<template v-if="!page.switchSourcesPricingOnly"> and from public reviews</template>, checked {{ reviewedDate }}.
          {{ page.competitor }} may have changed since; check the linked source.
        </p>
        <!-- Only when the competitor has an /alternatives/ page (appConfig.alternativesSlugs). -->
        <p v-if="hasAlternativesPage" class="cmp-why-more">
          Weighing more than two tools?
          <NuxtLink
            :to="localePath(`/alternatives/${pricingKey}`)"
            data-conversion-event="comparison_click"
            :data-conversion-label="`Alternatives ${page.competitor}`"
            :data-conversion-location="page.slug"
          >See every {{ page.competitor }} alternative we compared →</NuxtLink>
        </p>
      </div>
    </section>

    <!-- PHONE AND IPAD (App Store review quotes + Workspace369's mobile app facts) -->
    <section v-if="page.mobileApp" class="cmp-section cmp-rule">
      <div class="cmp-wrap">
        <header class="cmp-head">
          <p>Phone and iPad</p>
          <h2>{{ page.competitor }} vs Workspace369 on a phone or iPad</h2>
        </header>
        <div class="cmp-who-grid">
          <article class="cmp-who-card">
            <p class="cmp-who-label">{{ page.competitor }}'s app, in App Store reviews</p>
            <p class="cmp-who-copy">{{ page.mobileApp.intro }}</p>
            <blockquote v-for="quote in page.mobileApp.quotes" :key="quote.text" class="cmp-mobile-quote">
              <p>“{{ quote.text }}”</p>
              <footer>
                — {{ quote.attribution }} ·
                <a :href="quote.sourceUrl" target="_blank" rel="noopener noreferrer">{{ quote.sourceLabel }} ↗</a>
              </footer>
            </blockquote>
          </article>
          <article class="cmp-who-card cmp-who-us">
            <p class="cmp-who-label"><Logo class="cmp-logo" /> Workspace369 on iPhone and iPad</p>
            <ul class="cmp-mobile-list">
              <li v-for="point in workspaceMobileApp.points" :key="point">{{ point }}</li>
            </ul>
            <p class="cmp-who-meta">
              App Store listing, checked {{ workspaceMobileApp.store.checkedOn }}:
              <template v-for="row in workspaceMobileApp.store.rows" :key="row.device">{{ ' ' }}{{ row.device }}: {{ row.requirement }}</template>{{ ' ' }}
              <a :href="workspaceMobileApp.store.url" target="_blank" rel="noopener noreferrer">{{ workspaceMobileApp.store.label }} ↗</a>
            </p>
            <p class="cmp-who-meta">
              <NuxtLink :to="localePath(workspaceMobileApp.featurePath)">See the mobile app →</NuxtLink>
            </p>
          </article>
        </div>
        <p class="cmp-note">
          Quotes are from public App Store reviews of {{ page.competitor }}'s app, quoted as written. Each is one person's experience, and the app may have changed since.
        </p>
      </div>
    </section>

    <!-- TALK TO A PERSON (App Store review quotes + Workspace369's support rows from pricing-features.json) -->
    <section v-if="page.talkToPerson" class="cmp-section cmp-rule">
      <div class="cmp-wrap">
        <header class="cmp-head">
          <p>Support</p>
          <h2>Talk to a person</h2>
        </header>
        <div class="cmp-who-grid">
          <article class="cmp-who-card">
            <p class="cmp-who-label">{{ page.competitor }}'s support, in App Store reviews</p>
            <p class="cmp-who-copy">{{ page.talkToPerson.intro }}</p>
            <blockquote v-for="quote in page.talkToPerson.quotes" :key="quote.text" class="cmp-mobile-quote">
              <p>“{{ quote.text }}”</p>
              <footer>
                — {{ quote.attribution }} ·
                <a :href="quote.sourceUrl" target="_blank" rel="noopener noreferrer">{{ quote.sourceLabel }} ↗</a>
              </footer>
            </blockquote>
          </article>
          <article class="cmp-who-card cmp-who-us">
            <p class="cmp-who-label"><Logo class="cmp-logo" /> Workspace369 support</p>
            <ul class="cmp-mobile-list">
              <li v-for="point in workspaceSupport.points" :key="point">{{ point }}</li>
            </ul>
            <p class="cmp-who-copy">{{ page.talkToPerson.moving }}</p>
            <p class="cmp-who-meta">
              Email <a :href="`mailto:${workspaceSupport.email}`">{{ workspaceSupport.email }}</a> ·
              <NuxtLink :to="localePath(workspaceSupport.featurePath)">See support and onboarding →</NuxtLink>
            </p>
          </article>
        </div>
        <p class="cmp-note">
          Quotes are from public App Store reviews of {{ page.competitor }}'s app, quoted as written. Each is one person's experience, and support may have changed since.
        </p>
      </div>
    </section>

    <!-- PHONE AND IPAD, Workspace369 side only (pages in data/mobile-app-facts.ts mobileProofPaths) -->
    <section v-if="!page.mobileApp && mobileProofPaths.has(page.slug)" class="cmp-section cmp-rule">
      <div class="cmp-wrap">
        <header class="cmp-head">
          <p>Phone and iPad</p>
          <h2>Workspace369 on iPhone and iPad</h2>
        </header>
        <MobileAppProof :location="page.slug" />
      </div>
    </section>

    <!-- WHO IS X / WHO IS WS -->
    <section class="cmp-section" :class="page.switchReasons?.length ? 'cmp-rule' : 'cmp-tint'">
      <div class="cmp-wrap">
        <header class="cmp-head">
          <p>The two products</p>
          <h2>Who is {{ page.competitor }}? Who is Workspace369?</h2>
        </header>
        <div class="cmp-who-grid">
          <article class="cmp-who-card">
            <p class="cmp-who-label">{{ page.competitor }}</p>
            <p class="cmp-who-copy">{{ page.competitorSummary }}</p>
            <p class="cmp-who-meta">
              Category: {{ page.category || "Business software" }} ·
              <a :href="page.competitorUrl" target="_blank" rel="noopener noreferrer">{{ competitorHost }} ↗</a>
            </p>
          </article>
          <article class="cmp-who-card cmp-who-us">
            <p class="cmp-who-label"><Logo class="cmp-logo" /> Workspace369</p>
            <p class="cmp-who-copy">{{ page.workspaceSummary }}</p>
            <p class="cmp-who-meta">
              Client work operating system ·
              <NuxtLink :to="localePath('/workspace369-features')">see all features →</NuxtLink>
            </p>
          </article>
        </div>
      </div>
    </section>

    <!-- VENDOR FOCUS: one section only this competitor's page has (data/marketing-pages.js vendorFocus), so
         near-duplicate comparisons (wrike, smartsheet) each carry copy the other can't. -->
    <section v-if="page.vendorFocus" class="cmp-section" :class="page.switchReasons?.length ? 'cmp-tint' : 'cmp-rule'">
      <div class="cmp-wrap">
        <header class="cmp-head">
          <p>{{ page.vendorFocus.kicker }}</p>
          <h2>{{ page.vendorFocus.heading }}</h2>
        </header>
        <p class="cmp-focus-intro">{{ page.vendorFocus.intro }}</p>
        <div class="cmp-why-grid">
          <article v-for="item in page.vendorFocus.items" :key="item.title" class="cmp-why-card">
            <h3>{{ item.title }}</h3>
            <p class="cmp-why-evidence">{{ item.competitor }}</p>
            <p class="cmp-why-answer">
              <span class="cmp-why-label"><Logo class="cmp-logo" /> In Workspace369</span>
              {{ item.workspace }}
            </p>
          </article>
        </div>
        <p class="cmp-note">
          Source:
          <template v-for="(source, i) in page.vendorFocus.sources" :key="source.url"><template v-if="i">, </template><a :href="source.url" target="_blank" rel="noopener noreferrer">{{ source.label }}</a></template>,
          checked {{ page.vendorFocus.checkedOn }}.
        </p>
      </div>
    </section>

    <!-- CORE OPERATIONS -->
    <section class="cmp-section">
      <div class="cmp-wrap">
        <header class="cmp-head">
          <p>Feature comparison</p>
          <h2>Core operations, side by side</h2>
        </header>
        <div class="cmp-shell">
          <table class="cmp-table">
            <thead>
              <tr>
                <th scope="col">Area</th>
                <th scope="col">{{ page.competitor }}</th>
                <th scope="col" class="cmp-th-us"><Logo class="cmp-logo" /> Workspace369</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in coreRows" :key="row.area">
                <th scope="row">{{ row.area }}</th>
                <td>
                  <span class="cmp-m-label">{{ page.competitor }}</span>
                  {{ row.competitor }}
                </td>
                <td class="cmp-td-us">
                  <span class="cmp-m-label">Workspace369</span>
                  {{ row.workspace }}
                  <template v-for="link in row.links || []" :key="link.path">{{ ' ' }}<NuxtLink :to="localePath(link.path)" class="cmp-row-link">{{ link.label }} →</NuxtLink></template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <template v-if="billingRows.length">
          <h3 class="cmp-subhead">Client work and pricing</h3>
          <div class="cmp-shell">
            <table class="cmp-table">
              <tbody>
                <tr v-for="row in billingRows" :key="row.area">
                  <th scope="row">{{ row.area }}</th>
                  <td>
                    <span class="cmp-m-label">{{ page.competitor }}</span>
                    {{ row.competitor }}
                  </td>
                  <td class="cmp-td-us">
                    <span class="cmp-m-label">Workspace369</span>
                    {{ row.workspace }}
                    <template v-for="link in row.links || []" :key="link.path">{{ ' ' }}<NuxtLink :to="localePath(link.path)" class="cmp-row-link">{{ link.label }} →</NuxtLink></template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <p class="cmp-note">
          Comparison based on public product positioning and Workspace369's current feature set, reviewed {{ reviewedDate }}.
          Exact fit depends on your workflow, team size, and implementation needs.
        </p>
      </div>
    </section>

    <!-- PRICING -->
    <section v-if="pricingSnapshot" class="cmp-section cmp-tint">
      <div class="cmp-wrap">
        <header class="cmp-head">
          <p>Pricing and value</p>
          <h2>Compare the subscription, then compare the stack</h2>
        </header>
        <div class="cmp-pricing-grid">
          <div class="cmp-shell">
            <table class="cmp-table">
              <thead>
                <tr>
                  <th scope="col" colspan="2">
                    {{ page.competitor }}
                    <a
                      v-if="pricingSnapshot.pricingUrl"
                      :href="pricingSnapshot.pricingUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="cmp-src"
                    >source ↗</a>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="plan in pricingSnapshot.plans" :key="plan.name">
                  <th scope="row">{{ plan.name }}</th>
                  <td>{{ plan.price }}<span class="cmp-plan-note"> — {{ plan.note }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="cmp-shell cmp-shell-us">
            <table class="cmp-table">
              <thead>
                <tr>
                  <th scope="col" colspan="2" class="cmp-th-us">
                    <Logo class="cmp-logo" /> Workspace369
                    <NuxtLink :to="localePath('/pricing')" class="cmp-src">all plans →</NuxtLink>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="plan in workspacePricingSnapshot" :key="plan.name">
                  <th scope="row">{{ plan.name }}</th>
                  <td>{{ plan.price }}<span class="cmp-plan-note"> — {{ plan.note }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <p class="cmp-math">{{ pricingSnapshot.example }}</p>

        <template v-if="pricingSnapshot.teamCost">
          <h3 class="cmp-subhead">What your team pays</h3>
          <div class="cmp-shell">
            <table class="cmp-table cmp-team">
              <thead>
                <tr>
                  <th scope="col">Team size</th>
                  <th scope="col" class="cmp-th-us"><Logo class="cmp-logo" /> Workspace369</th>
                  <th scope="col">{{ page.competitor }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in pricingSnapshot.teamCost.rows" :key="row.people">
                  <th scope="row">{{ row.people === 1 ? "1 person" : `${row.people} people` }}</th>
                  <td class="cmp-td-us">
                    <span class="cmp-m-label">Workspace369</span>
                    <strong>{{ row.workspace.plan }}</strong>
                    <span class="cmp-plan-note">{{ row.workspace.monthly }}, or {{ row.workspace.yearly }}</span>
                  </td>
                  <td>
                    <span class="cmp-m-label">{{ page.competitor }}</span>
                    <strong>{{ row.competitor.plan }}</strong>
                    <span class="cmp-plan-note">{{ row.competitor.price }}<template v-if="row.competitor.note">. {{ row.competitor.note }}</template></span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="cmp-team-buys">{{ pricingSnapshot.teamCost.difference }}</p>
          <p class="cmp-team-foot">{{ pricingSnapshot.teamCost.footnote }}</p>
        </template>

        <h3 class="cmp-subhead">What the stack costs separately</h3>
        <div class="cmp-shell">
          <table class="cmp-table">
            <thead>
              <tr>
                <th scope="col">Workflow</th>
                <th scope="col">Separate stack</th>
                <th scope="col" class="cmp-th-us"><Logo class="cmp-logo" /> Workspace369</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in workspaceStackValueRows" :key="row.stack">
                <th scope="row">{{ row.stack }}</th>
                <td>
                  <span class="cmp-m-label">Separate stack</span>
                  {{ row.separateTools }}
                </td>
                <td class="cmp-td-us">
                  <span class="cmp-m-label">Workspace369</span>
                  {{ row.workspace }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="cmp-method">
          <p>
            Reviewed {{ reviewedDate }}. Pricing and packaging can change — verify current details with each vendor
            before deciding. The goal is a buyer-useful comparison, not a pretend scorecard.
          </p>
          <ul v-if="methodSources.length">
            <li v-for="source in methodSources" :key="source.url">
              <a :href="source.url" target="_blank" rel="noopener noreferrer">{{ source.label }} ↗</a>
              <span>{{ source.note }}</span>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- WHICH SHOULD YOU CHOOSE -->
    <section class="cmp-section">
      <div class="cmp-wrap">
        <header class="cmp-head">
          <p>Decision</p>
          <h2>Workspace369 vs {{ page.competitor }}: which should you choose?</h2>
        </header>
        <div class="cmp-decision-grid">
          <article class="cmp-decision">
            <h3>Choose {{ page.competitor }} if…</h3>
            <ul>
              <li v-for="item in competitorChoose" :key="item">{{ item }}</li>
            </ul>
          </article>
          <article class="cmp-decision cmp-decision-us">
            <h3><Logo class="cmp-logo" /> Choose Workspace369 if…</h3>
            <ul>
              <li v-for="item in page.choose" :key="item">
                <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M13.3 4.3 6.4 11.2 2.7 7.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                {{ item }}
              </li>
            </ul>
          </article>
        </div>
      </div>
    </section>

    <!-- SWITCHING -->
    <section class="cmp-section cmp-tint">
      <div class="cmp-wrap">
        <header class="cmp-head">
          <p>Switching</p>
          <h2>Switching to Workspace369 takes four steps</h2>
        </header>
        <ol class="cmp-steps">
          <li v-for="(step, i) in switchSteps" :key="step.title">
            <span class="cmp-step-num">{{ String(i + 1).padStart(2, "0") }}</span>
            <div>
              <h3>
                <NuxtLink v-if="step.path" :to="localePath(step.path)" class="cmp-step-link">{{ step.title }}</NuxtLink>
                <template v-else>{{ step.title }}</template>
              </h3>
              <p>{{ step.copy }}</p>
            </div>
          </li>
        </ol>
      </div>
    </section>

    <!-- PRODUCT TOUR -->
    <section class="cmp-section">
      <div class="cmp-wrap">
        <header class="cmp-head">
          <p>Product tour</p>
          <h2>See the client-work loop inside Workspace369</h2>
        </header>
        <div class="cmp-shots">
          <figure v-for="shot in shots" :key="shot.src" class="cmp-shot">
            <img :src="shot.src" :alt="shot.alt" :class="shot.dark ? 'cmp-shot-light' : null" loading="lazy" width="1600" height="1000" />
            <img v-if="shot.dark" :src="shot.dark" :alt="shot.alt" class="cmp-shot-dark" loading="lazy" width="1600" height="1000" />
            <figcaption>{{ shot.caption }}</figcaption>
          </figure>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="cmp-section cmp-tint">
      <div class="cmp-wrap cmp-wrap-narrow">
        <header class="cmp-head">
          <p>FAQ</p>
          <h2>Frequently asked questions</h2>
        </header>
        <dl class="cmp-faq">
          <div v-for="item in page.faq" :key="item.q">
            <dt>{{ item.q }}</dt>
            <dd>{{ item.a }}</dd>
          </div>
        </dl>
      </div>
    </section>

    <!-- FINAL CTA -->
    <section class="cmp-final" data-sticky-cta-hide>
      <div class="cmp-wrap">
        <div class="cmp-final-panel">
          <Logo class="cmp-final-logo" />
          <h2>Run the comparison with your own client work.</h2>
          <p>{{ pricingSnapshot?.valueHeadline }}</p>
          <div class="cmp-cta-row cmp-cta-center">
            <a
              :href="signupUrl('https://app.workspace369.com/signup')"
              class="cmp-btn cmp-btn-accent"
              data-conversion-event="start_free_click"
              :data-conversion-label="`${page.h1} final CTA`"
              :data-conversion-location="page.slug"
              :data-conversion-variant="ctaVariant || undefined"
            >Start free trial</a>
            <NuxtLink :to="localePath('/pricing')" class="cmp-link">See pricing →</NuxtLink>
          </div>
          <TrialDisclosure centered on-light />
        </div>
      </div>
    </section>

    <RelatedPages
      :path="page.slug"
      :title="page.h1"
      :exclude="(page.relatedLinks || []).map((link) => link.path)"
    />

    <Cta03 />

    <!-- Mobile sticky CTA -->
    <div class="cmp-sticky" :class="{ 'is-off': ownCtaOnScreen }" :aria-hidden="ownCtaOnScreen ? 'true' : undefined">
      <div>
        <strong>Workspace369 vs {{ page.competitor }}</strong>
        <TrialDisclosure compact on-light />
      </div>
      <div class="cmp-sticky-actions">
        <NuxtLink
          :to="localePath('/pricing')"
          class="cmp-sticky-pricing"
          :tabindex="ownCtaOnScreen ? -1 : undefined"
          data-conversion-event="pricing_click"
          :data-conversion-label="`${page.h1} sticky pricing`"
          :data-conversion-location="page.slug"
        >{{ localeUi.nav.pricing }}</NuxtLink>
      <a
        :href="signupUrl('https://app.workspace369.com/signup')"
        class="cmp-sticky-btn"
        :tabindex="ownCtaOnScreen ? -1 : undefined"
        data-conversion-event="start_free_click"
        :data-conversion-label="`${page.h1} sticky CTA`"
        :data-conversion-location="page.slug"
        :data-conversion-variant="ctaVariant || undefined"
      >Start free trial</a>
      </div>
    </div>
  </main>
</template>

<script setup>
import Logo from "~/components/Logo.vue";
import {
  comparisonPricingBySlug,
  workspacePricingSnapshot,
  workspaceStackValueRows,
} from "#build/page-data/comparison-pricing.mjs";
import releaseLastmods from "#build/page-data/release-lastmod.mjs";
import { localizedUrl } from "~/data/locales";
import { seoRoutes } from "~/data/seo-routes";
import { mobileProofPaths, workspaceMobileApp } from "~/data/mobile-app-facts";
import { workspaceSupport } from "~/data/support-facts";

const props = defineProps({
  page: {
    type: Object,
    required: true,
  },
});

const { addReferralToUrl } = useReferral();
// Pages that set utmSource tag their sign-up links so the source survives without cookie consent.
const signupUrl = (href) => {
  const url = new URL(href);
  if (!props.page.utmSource || url.origin !== "https://app.workspace369.com" || url.pathname !== "/signup") return addReferralToUrl(href);
  url.searchParams.set("utm_source", props.page.utmSource);
  url.searchParams.set("utm_medium", "organic_page");
  return addReferralToUrl(url.toString());
};
const { canonicalLocaleCode, localePath, localeUi } = useWorkspaceLocale();
const { enrolled: ctaEnrolled, variant: ctaVariant, label: ctaLabel } = useCtaTest(() => props.page.slug);
// Comparisons with phone apps sell calls and texts, which start at Voyager, so their hero opens the
// Voyager trial too (AGENTS.md: calls-and-texts CTAs go to ?plan=voyager). The CTA test pages already do.
const voyagerHeroPaths = new Set(["/compare/google-voice", "/compare/quo", "/compare/ringcentral", "/compare/dialpad", "/compare/nextiva"]);
const heroPlan = computed(() => (ctaEnrolled.value || voyagerHeroPaths.has(props.page.slug) ? "voyager" : ""));

// The phone sticky bar stays away while another start button is on screen (the hero's, the final panel's,
// Cta03's) or the footer is, so only one start button shows at a time. Assume one is on screen until the
// observer says otherwise (no flash).
const ownCtaOnScreen = ref(true);
let ownCtaObserver = null;
onMounted(() => {
  // Watch the hero button itself: on phones its row is display: contents and has no box to observe.
  const targets = [document.querySelector(".cmp-hero .cmp-btn"), ...document.querySelectorAll("[data-sticky-cta-hide], footer")].filter(Boolean);
  if (!targets.length) { ownCtaOnScreen.value = false; return; }
  const showing = new Set();
  ownCtaObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) entry.isIntersecting ? showing.add(entry.target) : showing.delete(entry.target);
    ownCtaOnScreen.value = showing.size > 0;
  });
  targets.forEach((el) => ownCtaObserver.observe(el));
});
onBeforeUnmount(() => ownCtaObserver?.disconnect());
const siteUrl = "https://workspace369.com";
const pricingKey = computed(() => props.page.pricingKey || props.page.slug.split("/").pop());
const pricingSnapshot = computed(() => comparisonPricingBySlug[pricingKey.value]);
const alternativesSlugs = useAppConfig().alternativesSlugs;
const hasAlternativesPage = computed(() => alternativesSlugs.includes(pricingKey.value));
// Every competitor statement on the page rests on a public page of theirs: the sources listed in
// the entry, plus their features page and pricing page, each with the date we read them.
const methodSources = computed(() => {
  const list = [...(props.page.sources || [])];
  const seen = new Set(list.map((source) => source.url));
  const add = (label, url) => {
    if (url && !seen.has(url)) {
      seen.add(url);
      list.push({ label, url, note: `Read ${reviewedDate.value}.` });
    }
  };
  add(`${props.page.competitor} features`, props.page.competitorUrl);
  add(`${props.page.competitor} pricing`, pricingSnapshot.value?.pricingUrl);
  return list;
});
const reviewedDate = computed(
  () => props.page.reviewedDate || pricingSnapshot.value?.reviewedDate,
);
const canonicalUrl = computed(() => localizedUrl(siteUrl, props.page.slug, canonicalLocaleCode.value));
// Same date as the sitemap and app.vue's Article and WebPage: a newer release card, else the
// seo-routes lastmod, so every JSON-LD node on the page states one modified date.
const schemaLastmod = computed(
  () =>
    releaseLastmods[props.page.slug] ||
    seoRoutes.find((entry) => entry.path === props.page.slug)?.lastmod ||
    props.page.dateModified ||
    "2026-06-03"
);

const competitorHost = computed(() => {
  try {
    return new URL(props.page.competitorUrl).hostname.replace(/^www\./, "");
  } catch {
    return props.page.competitorUrl;
  }
});

// High-level "at a glance" rows, derived from the entry's existing fit/pricing data
const glanceRows = computed(() =>
  [
    {
      label: "Best for",
      competitor:
        props.page.rows.find((r) => /primary fit/i.test(r.area))?.competitor || props.page.competitorSummary,
      workspace:
        props.page.rows.find((r) => /primary fit/i.test(r.area))?.workspace || props.page.workspaceSummary,
    },
    {
      label: "Pricing model",
      competitor: pricingSnapshot.value?.summary || "See vendor pricing",
      workspace: "Plans per workspace with seats included: $29/mo for 1 seat up to $699/mo for 50 seats, with texts and calls from Voyager and IVR and call queues from Specialist.",
    },
    {
      label: "Client layer",
      competitor: props.page.rows.find((r) => /client/i.test(r.area))?.competitor,
      workspace: "CRM, client portal, proposals, invoices, and reporting in one record, with payment plans from Specialist.",
    },
    {
      label: "Automations",
      competitor: props.page.rows.find((r) => /automation/i.test(r.area))?.competitor,
      workspace: "No-code workflow automations for emails, tasks, reminders, and internal notifications, with SMS steps from Voyager.",
    },
    // Hide a row when the entry has no competitor data, rather than implying the competitor lacks it
  ].filter((row) => row.competitor),
);

const coreRows = computed(() =>
  props.page.rows.filter((r) => /primary fit|project|automation|view/i.test(r.area)),
);
const billingRows = computed(() =>
  props.page.rows.filter((r) => !/primary fit|project|automation|view/i.test(r.area)),
);

const competitorChoose = computed(() => props.page.chooseCompetitor || [props.page.competitorSummary]);

const switchSteps = [
  {
    title: "Start your free trial",
    copy: "Your workspace is ready in minutes — pick a plan later, no migration call required.",
  },
  {
    title: "Import your clients",
    copy: "Bring clients, contacts, and open invoices in with CSV import instead of retyping anything.",
    path: "/integrations/csv-import",
  },
  {
    title: "Connect your tools",
    copy: "Hook up Google Calendar, your inbox, and Stripe so schedules, conversations, and payments land in one place.",
    path: "/integrations/google-calendar",
  },
  {
    title: "Invite the team and go live",
    copy: "Run your next client project end-to-end — request to proposal to invoice — and cancel the old stack when it sticks.",
  },
];

const shots = [
  {
    src: "/home/screens/projects-board-light-clean.webp",
    alt: "Workspace369 project board with Kanban columns attached to client work",
    caption: "Project boards with Kanban and Gantt views — attached to the client record, not a separate tool.",
  },
  {
    src: "/home/screens/invoice-list-light-clean.webp",
    alt: "Workspace369 invoice list showing invoice status beside client names",
    caption: "Invoices, payment plans, and payment status beside every client — no separate billing app.",
  },
  {
    src: "/home/screens/inbox-light-clean.webp",
    dark: "/home/screens/inbox-dark-clean.webp",
    alt: "Workspace369 shared inbox with client conversations and AI summaries",
    caption: "Shared inbox, texts and calls from Voyager, AI call summaries from Specialist — the conversation lives beside the work.",
  },
];

useHead(() => ({
  title: props.page.metaTitle,
  meta: [
    { name: "description", content: props.page.description },
    { name: "twitter:title", content: props.page.metaTitle },
    { name: "twitter:description", content: props.page.description },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: props.page.metaTitle },
    { property: "og:description", content: props.page.description },
  ],
  link: [{ key: "canonical", rel: "canonical", href: canonicalUrl.value }],
  script: [
    {
      key: `workspace369-comparison-${props.page.slug}`,
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebPage",
            "@id": `${canonicalUrl.value}#comparison`,
            name: props.page.metaTitle,
            url: canonicalUrl.value,
            description: props.page.description,
            dateModified: schemaLastmod.value,
            reviewedBy: { "@id": `${siteUrl}/#organization` },
            citation: props.page.sources?.map((source) => source.url)
              || pricingSnapshot.value?.pricingUrl,
            about: [
              { "@id": `${siteUrl}/#software` },
              {
                "@type": "Thing",
                name: props.page.competitor,
                url: props.page.competitorUrl,
              },
            ],
            mentions: [
              {
                "@type": "Thing",
                name: props.page.competitor,
                url: props.page.competitorUrl,
              },
            ],
          },
          {
            "@type": "FAQPage",
            "@id": `${canonicalUrl.value}#faq`,
            mainEntity: props.page.faq.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.a,
              },
            })),
          },
        ],
      }),
    },
  ],
}));
</script>

<style scoped>
/* Sections below the hero: the browser skips their layout and paint until they near the
   viewport (they stay searchable and accessible) and remembers their height once rendered.
   That work used to delay the first paint, whose largest text is the page's LCP.
   The FAQ (last section before .cmp-final) and the section above it render normally: a deep link
   lands there, and resizing a placeholder right above the target counted as a layout shift. */
.cmp > section:not(.cmp-hero):not(:has(+ .cmp-final)):not(:has(+ section + .cmp-final)) {
  content-visibility: auto;
  contain-intrinsic-size: auto 900px;
}

.cmp {
  background: #fff;
  color: #111827;
}

.cmp-wrap {
  max-width: 64rem;
  margin-inline: auto;
  padding-inline: 1.25rem;
}

@media (min-width: 640px) {
  .cmp-wrap { padding-inline: 1.75rem; }
}

.cmp-wrap-narrow { max-width: 44rem; }

/* HERO */
.cmp-hero { padding: 7.5rem 0 3rem; }

@media (min-width: 768px) {
  .cmp-hero { padding: 9rem 0 3.5rem; }
}

.cmp-eyebrow {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  color: #6b7280;
  font-size: 0.73rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.cmp-eyebrow i {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background: #0e7490;
}

.cmp-hero h1 {
  margin-top: 1.1rem;
  max-width: 50rem;
  font-size: clamp(2.1rem, 4.8vw, 3.5rem);
  font-weight: 900;
  letter-spacing: -0.03em;
  line-height: 1.04;
  text-wrap: balance;
}

.cmp-lede {
  margin-top: 1.2rem;
  max-width: 40rem;
  color: #4b5563;
  font-size: 1.04rem;
  line-height: 1.7;
}

.cmp-answer {
  margin-top: 1.1rem;
  max-width: 40rem;
  border-left: 3px solid #0e7490;
  padding-left: 0.9rem;
  color: #1f2937;
  font-size: 0.98rem;
  line-height: 1.65;
}

.cmp-answer--first {
  margin-top: 1rem;
  margin-bottom: 0.4rem;
}
.cmp-answer-label {
  margin-bottom: 0.2rem;
  color: #0e7490;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.cmp-cta-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.2rem;
  margin-top: 1.8rem;
}

.cmp-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.9rem;
  border-radius: 0.55rem;
  background: #111827;
  color: #fff;
  padding: 0.72rem 1.45rem;
  font-size: 0.92rem;
  font-weight: 800;
  transition: background-color 150ms ease;
}

.cmp-btn:hover { background: #0e7490; }

.cmp-btn-accent { background: #0e7490; }
.cmp-btn-accent:hover { background: #155e75; }

.cmp-link { display: inline-block; padding-block: 0.7rem; color: #0e7490; font-size: 0.92rem; font-weight: 800; } /* 44px+ tap target (was 28px) */
.cmp-link:hover { text-decoration: underline; }

.cmp-trust {
  margin-top: 1.4rem;
  max-width: 40rem;
  color: #6b7280;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.6;
}

/* SECTIONS */
.cmp-section { padding: 3.25rem 0; }
@media (min-width: 768px) { .cmp-section { padding: 4.25rem 0; } }

.cmp-tint { background: #fafafa; border-block: 1px solid #f0f1f3; }

.cmp-head { max-width: 44rem; margin-bottom: 1.9rem; }

.cmp-head p {
  color: #0e7490;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.cmp-head h2 {
  margin-top: 0.55rem;
  font-size: clamp(1.6rem, 3vw, 2.3rem);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.08;
  overflow-wrap: break-word;
}

.cmp-subhead {
  margin: 2.25rem 0 1rem;
  font-size: 1.05rem;
  font-weight: 900;
  letter-spacing: -0.01em;
}

.cmp-logo {
  width: 1.05em;
  height: 1.05em;
  display: inline-block;
  vertical-align: -0.16em;
}

/* TABLE SHELL */
.cmp-shell {
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  background: #fff;
  overflow: hidden;
}

.cmp-shell-us { border-color: rgb(14 116 144 / 0.35); }

.cmp-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.9rem;
}

.cmp-table thead th {
  border-bottom: 2px solid #111827;
  padding: 0.85rem 1.1rem;
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.07em;
  text-align: left;
  text-transform: uppercase;
}

.cmp-table thead .cmp-th-us { color: #0e7490; }

.cmp-table tbody tr + tr th,
.cmp-table tbody tr + tr td {
  border-top: 1px solid #eef0f3;
}

.cmp-table tbody th {
  padding: 0.9rem 1.1rem;
  color: #111827;
  font-weight: 800;
  text-align: left;
  vertical-align: top;
}

.cmp-table tbody td {
  padding: 0.9rem 1.1rem;
  color: #4b5563;
  line-height: 1.6;
  vertical-align: top;
}

.cmp-table tbody .cmp-td-us {
  background: #f0f9fc;
  color: #111827;
  font-weight: 600;
}

.cmp-plan-note {
  display: block;
  margin-top: 0.25rem;
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.5;
}

.cmp-m-label {
  display: block;
  margin-bottom: 0.2rem;
  color: #4b5563;
  font-size: 0.66rem;
  font-weight: 900;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

/* Mobile: stack table rows into blocks — no horizontal scrolling */
@media (max-width: 819px) {
  .cmp-table thead { display: none; }
  .cmp-table,
  .cmp-table tbody,
  .cmp-table tr,
  .cmp-table th,
  .cmp-table td { display: block; width: 100%; }
  .cmp-table tbody tr + tr { border-top: 1px solid #e5e7eb; }
  .cmp-table tbody tr + tr th,
  .cmp-table tbody tr + tr td { border-top: none; }
  .cmp-table tbody th { padding-bottom: 0; }
  .cmp-table tbody td { padding-top: 0.45rem; }
  .cmp-table tbody .cmp-td-us { margin-top: 0.45rem; }
}

@media (min-width: 820px) {
  .cmp-m-label { display: none; }
}

.cmp-note {
  margin-top: 1.2rem;
  max-width: 46rem;
  color: #6b7280;
  font-size: 0.78rem;
  line-height: 1.6;
}

/* WHO CARDS */
.cmp-who-grid { display: grid; grid-template-columns: minmax(0, 1fr); gap: 1.1rem; }
@media (min-width: 820px) { .cmp-who-grid { grid-template-columns: 1fr 1fr; } }

.cmp-who-card {
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  background: #fff;
  padding: 1.6rem 1.7rem;
}

.cmp-who-us {
  border-color: rgb(14 116 144 / 0.35);
  background: #f7fcfe;
}

.cmp-who-label {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: #6b7280;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.cmp-who-us .cmp-who-label { color: #0e7490; }

.cmp-who-copy {
  margin-top: 0.9rem;
  color: #374151;
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.65;
}

.cmp-who-meta {
  margin-top: 1rem;
  color: var(--c-ink3);
  font-size: 0.78rem;
  font-weight: 600;
}

.cmp-who-meta a { color: #0e7490; font-weight: 800; }

/* PHONE AND IPAD */
.cmp-mobile-quote {
  margin-top: 1rem;
  border-left: 3px solid #e5e7eb;
  padding-left: 0.9rem;
}

.cmp-mobile-quote p {
  color: #1f2937;
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.6;
}

.cmp-mobile-quote footer {
  margin-top: 0.3rem;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.5;
}

.cmp-mobile-quote footer a { text-decoration: underline; text-underline-offset: 2px; }
.cmp-mobile-quote footer a:hover { color: #0e7490; }

.cmp-mobile-list {
  margin-top: 0.9rem;
  padding-left: 1.1rem;
  list-style: disc;
  color: #374151;
  font-size: 0.92rem;
  font-weight: 600;
  line-height: 1.6;
}

.cmp-mobile-list li + li { margin-top: 0.35rem; }

/* WHY PEOPLE SWITCH */
.cmp-rule { border-top: 1px solid #f0f1f3; }
.cmp-focus-intro { max-width: 46rem; margin: -0.9rem 0 1.6rem; color: var(--c-ink2, #4b5563); font-size: 0.95rem; line-height: 1.7; }
.cmp-why-grid { display: grid; gap: 1.1rem; }
.cmp-why-more { margin-top: 0.9rem; color: #475569; font-size: 0.92rem; }
.cmp-why-more a { color: #0e7490; font-weight: 700; }
.cmp-why-more a:hover { text-decoration: underline; }
.cmp-why-more a:focus-visible { outline: 2px solid #0e7490; outline-offset: 2px; border-radius: 2px; }
@media (min-width: 900px) { .cmp-why-grid { grid-template-columns: repeat(3, 1fr); } }

.cmp-why-card {
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  background: #fff;
  padding: 1.5rem 1.5rem 1.35rem;
}

.cmp-why-card h3 {
  font-size: 1.05rem;
  font-weight: 900;
  letter-spacing: -0.01em;
  line-height: 1.3;
}

.cmp-why-evidence {
  margin: 0.7rem 0 1rem;
  color: #4b5563;
  font-size: 0.88rem;
  font-weight: 600;
  line-height: 1.6;
}

.cmp-why-src {
  display: inline-block;
  margin-top: 0.35rem;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.cmp-why-src:hover { color: #0e7490; }

.cmp-why-answer {
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px dashed rgb(14 116 144 / 0.3);
  color: #1f2937;
  font-size: 0.88rem;
  font-weight: 700;
  line-height: 1.6;
}

.cmp-why-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.35rem;
  color: #0e7490;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

/* PRICING */
.cmp-pricing-grid { display: grid; gap: 1.1rem; }
@media (min-width: 820px) { .cmp-pricing-grid { grid-template-columns: 1fr 1fr; } }

.cmp-src {
  margin-left: 0.5rem;
  color: #6b7280;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.cmp-src:hover { color: #0e7490; }

.cmp-math {
  margin-top: 2rem;
  border: 1px solid rgb(14 116 144 / 0.25);
  border-radius: 0.9rem;
  background: #eef7fa;
  padding: 1.2rem 1.35rem;
  color: #164e57;
  font-size: 0.98rem;
  font-weight: 650;
  line-height: 1.65;
}

.cmp-team strong { color: #111827; font-weight: 800; }
@media (min-width: 820px) {
  .cmp-team { table-layout: fixed; }
  .cmp-team thead th:first-child { width: 9rem; }
}
.cmp-team-buys {
  margin-top: 1rem;
  max-width: 52rem;
  color: #374151;
  font-size: 0.92rem;
  line-height: 1.6;
}
.cmp-team-foot {
  margin-top: 0.5rem;
  max-width: 52rem;
  color: #6b7280;
  font-size: 0.78rem;
  line-height: 1.55;
}

.cmp-method {
  margin-top: 1.6rem;
  border-top: 1px solid #e5e7eb;
  padding-top: 1.2rem;
}

.cmp-method p {
  max-width: 46rem;
  color: #6b7280;
  font-size: 0.8rem;
  line-height: 1.6;
}

.cmp-method ul {
  display: grid;
  gap: 0.5rem;
  margin-top: 0.9rem;
}

.cmp-method li {
  display: flex;
  flex-wrap: wrap;
  gap: 0.15rem 0.6rem;
  align-items: baseline;
}

.cmp-method a {
  color: #0e7490;
  font-size: 0.8rem;
  font-weight: 800;
}

.cmp-method span { color: #6b7280; font-size: 0.76rem; }

/* DECISION */
.cmp-decision-grid { display: grid; gap: 1.1rem; }
@media (min-width: 820px) { .cmp-decision-grid { grid-template-columns: 1fr 1fr; } }

.cmp-decision {
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1.6rem 1.7rem;
}

.cmp-decision-us {
  border-color: rgb(14 116 144 / 0.4);
  background: #f7fcfe;
}

.cmp-decision h3 {
  display: flex;
  text-wrap: balance; /* pretty still left "if…" alone on line 2 at 320px/125% ("Choose HoneyBook if…") */
  align-items: center;
  gap: 0.45rem;
  font-size: 1.05rem;
  font-weight: 900;
}

/* The logo keeps its size beside a wrapping heading; on the narrowest phones the
   heading steps down so "Choose Workspace369 if…" fits on two lines. */
.cmp-decision h3 .cmp-logo { flex-shrink: 0; }

@media (max-width: 359px) {
  .cmp-decision h3 { font-size: min(1.05rem, 5.6vw); }
}

.cmp-decision ul {
  display: grid;
  gap: 0.7rem;
  margin-top: 1.1rem;
}

.cmp-decision li {
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  color: #4b5563;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.55;
}

.cmp-decision li svg {
  width: 0.95rem;
  height: 0.95rem;
  flex-shrink: 0;
  margin-top: 0.16rem;
  color: #0e7490;
}

/* STEPS */
.cmp-steps { display: grid; gap: 0.9rem; }
@media (min-width: 760px) { .cmp-steps { grid-template-columns: 1fr 1fr; } }

.cmp-steps li {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  background: #fff;
  padding: 1.3rem 1.4rem;
}

.cmp-step-num {
  color: #0e7490;
  font-size: 0.85rem;
  font-weight: 900;
  font-variant-numeric: tabular-nums;
}

.cmp-steps h3 { font-size: 0.98rem; font-weight: 800; }

.cmp-step-link { color: #0e7490; }
.cmp-step-link:hover { text-decoration: underline; }
.cmp-row-link { color: #0e7490; font-weight: 600; }
.cmp-row-link:hover { text-decoration: underline; }

.cmp-steps p {
  margin-top: 0.4rem;
  color: #4b5563;
  font-size: 0.87rem;
  line-height: 1.6;
}

/* SCREENSHOTS */
.cmp-shots { display: grid; gap: 1.4rem; }

.cmp-shot {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  background: #fff;
}

.cmp-shot img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  object-position: top center;
}

.cmp-shot figcaption {
  border-top: 1px solid #eef0f3;
  padding: 0.9rem 1.15rem;
  color: #4b5563;
  font-size: 0.87rem;
  font-weight: 600;
  line-height: 1.55;
}

/* FAQ */
.cmp-faq div {
  border-top: 1px solid #e5e7eb;
  padding: 1.15rem 0;
}

.cmp-faq dt { font-size: 0.98rem; font-weight: 800; }

.cmp-faq dd {
  overflow-wrap: anywhere;
  margin-top: 0.5rem;
  color: #4b5563;
  font-size: 0.9rem;
  line-height: 1.65;
}

/* FINAL CTA */
.cmp-final {
  border-top: 1px solid #e5e7eb;
  padding: 4rem 0 5rem;
}

.cmp-final-panel {
  border: 1px solid #d7ebf2;
  border-radius: 1.25rem;
  background: linear-gradient(160deg, #f0f9fc 0%, #f7fcfe 100%);
  padding: 2.75rem 2.25rem;
  text-align: center;
}

.cmp-final-logo {
  width: 2.6rem;
  height: 2.6rem;
  margin-inline: auto;
  color: #0e7490;
}

.cmp-final-panel h2 {
  margin: 1rem auto 0;
  max-width: 36rem;
  font-size: clamp(1.6rem, 3vw, 2.3rem);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.12;
  text-wrap: balance;
}

.cmp-final-panel > p {
  margin: 0.8rem auto 0;
  max-width: 34rem;
  color: #4b5563;
  font-size: 0.95rem;
  line-height: 1.6;
}

.cmp-cta-center { justify-content: center; }

/* Phones: the final panel's trial line sits directly under its button (legal B1.1) and "See pricing" moves
   below it instead of wrapping in between, as in the hero. Wider screens keep the button row. */
@media (max-width: 767px) {
  .cmp-final-panel { display: flex; flex-direction: column; align-items: center; }
  .cmp-final-panel > * { order: 1; }
  .cmp-final-panel > .cmp-cta-row { display: contents; }
  .cmp-final-panel > .cmp-cta-row > .cmp-btn { order: 2; margin-top: 1.8rem; }
  .cmp-final-panel > :deep(.trial-disclosure) { order: 3; }
  .cmp-final-panel > .cmp-cta-row > .cmp-link { order: 4; margin-top: 0.9rem; }
}

/* MOBILE STICKY CTA */
.cmp-sticky {
  position: fixed;
  inset-inline: 0;
  bottom: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.9rem;
  border-top: 1px solid #e5e7eb;
  /* Opaque: at 94% white the hero's text showed through behind the trial line. */
  background: #ffffff;
  padding: 0.7rem 1rem calc(0.7rem + env(safe-area-inset-bottom));
}

.cmp-sticky strong {
  display: block;
  color: #111827;
  font-size: 0.82rem;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cmp-sticky :deep(.trial-disclosure.compact) {
  /* The trial line beside the button: TrialDisclosure's approved short form (B1.1), never under 12px. */
  margin: 0;
  line-height: 1.35;
}

.cmp-sticky-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.5rem;
  border-radius: 0.55rem;
  background: #0e7490;
  color: #fff;
  padding: 0.5rem 1.05rem;
  font-size: 0.84rem;
  font-weight: 800;
  white-space: nowrap;
}

/* On phones (below 480px) the bar keeps the trial line and drops the title, which repeats the H1. */
@media (max-width: 479px) {
  .cmp-sticky strong { display: none; }
}

.cmp-sticky { transition: transform 0.2s ease, visibility 0.2s; }
.cmp-sticky.is-off { transform: translateY(110%); visibility: hidden; }

@media (min-width: 820px) {
  .cmp-sticky { display: none; }
}

/* Phones: the start button and its trial line come straight after the H1, then the answer
   (whiteboard 2026-10-30, option A). Visual order only: the HTML keeps the answer first. */
@media (max-width: 767px) {
  /* The row dissolves (display: contents) so the trial line can sit directly under the button
     (legal B1.1) and "See pricing" moves below it instead of wrapping in between. */
  .cmp-hero-copy { display: flex; flex-direction: column; }
  .cmp-hero-copy > * { order: 5; }
  .cmp-hero-copy > h1 { order: 1; }
  .cmp-hero-copy > .cmp-cta-row { display: contents; }
  .cmp-hero-copy > .cmp-cta-row > .cmp-btn { order: 2; align-self: flex-start; margin-top: 1.25rem; }
  /* TrialDisclosure renders a fragment, so it carries no scope id: reach it with :deep(). */
  .cmp-hero-copy > :deep(.trial-disclosure) { order: 3; }
  .cmp-hero-copy > .cmp-cta-row > .cmp-link { order: 4; align-self: flex-start; margin-top: 0.75rem; }
  .cmp-hero-copy > .cmp-answer, .cmp-hero-copy > .cmp-lede { margin-top: 1.5rem; }
}

@media (prefers-reduced-motion: reduce) {
  .cmp-sticky { transition: none; }
}

@media (max-width: 819px) {
  .cmp { padding-bottom: 4.25rem; }
}

/* ---- Proposal restyle (approved neutral app style: #171717 primary, #e6e6e6 hairlines, 6px controls,
   10px surfaces; no kicker above the headline, no pills, no teal accent bars). Light and dark. ---- */
.cmp {
  /* ink3 is the quiet label colour; #666 keeps it at 4.5:1 or better on the tinted Workspace369 cards. */
  --c-ink: #0a0a0a; --c-ink2: #4f4f4f; --c-ink3: #666666; --c-edge: #e6e6e6; --c-canvas: #f7f7f7;
  --c-page: #fff; --c-primary: #171717; --c-on-primary: #fafafa; --c-us: rgb(10 10 10 / 0.04);
  background: var(--c-page);
  color: var(--c-ink);
}
.dark .cmp {
  --c-ink: #fafafa; --c-ink2: #c2c2c2; --c-ink3: #9a9a9a; --c-edge: #2c2c2c; --c-canvas: #1a1a1a;
  --c-page: #121212; --c-primary: #fafafa; --c-on-primary: #171717; --c-us: rgb(250 250 250 / 0.06);
}
.cmp-hero-grid { max-width: 78rem; display: grid; grid-template-columns: minmax(0, 1fr); gap: 2.5rem; align-items: center; }
/* Translated editions carry long compounds (German, Finnish) that are wider than a phone column on their own. They wrap
   rather than hyphenate: auto hyphenation split brand names (Works-pace369, Honey-Book). */
.cmp :is(h1, h2, h3, p, td, th, li):not(:lang(en)) { hyphens: manual; overflow-wrap: break-word; }
@media (min-width: 1100px) {
  .cmp-hero-grid { grid-template-columns: minmax(0, 33rem) minmax(0, 1fr); gap: 3.5rem; }
}
.cmp-hero { padding: 8.5rem 0 2.5rem; }
@media (min-width: 768px) { .cmp-hero { padding: 9rem 0 3rem; } }
.cmp-hero h1 { margin-top: 0; font-weight: 780; letter-spacing: -0.01em; line-height: 1.05; font-size: clamp(2rem, 4.2vw, 3.25rem); }
.cmp-lede { color: var(--c-ink2); font-size: 1rem; }
.cmp-answer { border: 1px solid var(--c-edge); border-radius: 8px; background: var(--c-canvas); padding: 0.9rem 1rem; color: var(--c-ink); font-size: 0.95rem; }
.cmp-answer-label { color: var(--c-ink3); font-size: 0.8rem; font-weight: 600; letter-spacing: 0; text-transform: none; }
.cmp-btn { border-radius: 6px; background: var(--c-primary); color: var(--c-on-primary); font-weight: 650; }
.cmp-btn:hover { background: var(--c-primary); opacity: 0.9; }
.cmp-link { color: var(--c-ink); font-weight: 600; }
.cmp-trust { color: var(--c-ink3); font-weight: 500; }
.cmp-hero-shot { margin: 0; overflow: hidden; border: 1px solid var(--c-edge); border-radius: 10px; background: var(--c-canvas); box-shadow: 0 30px 60px -30px rgb(15 23 42 / 0.22); aspect-ratio: 19 / 16; align-self: start; }
.cmp-hero-shot img { display: block; width: 205%; max-width: none; height: auto; } /* crop to the record and its invoices; the screenshot's right rail is out of frame */
.cmp-shot-dark { display: none !important; }
.dark .cmp-shot-light { display: none !important; }
.dark .cmp-shot-dark { display: block !important; }
.dark .cmp-hero-shot { border-color: #3a3a3a; box-shadow: none; }
.cmp-tint { background: var(--c-canvas); border-block: 1px solid var(--c-edge); }
.cmp-rule { border-top: 1px solid var(--c-edge); }
.cmp-head p { color: var(--c-ink3); font-size: 0.8rem; font-weight: 600; letter-spacing: 0; text-transform: none; }
.cmp-head h2 { font-weight: 750; letter-spacing: -0.01em; }
.cmp-shell { border-color: var(--c-edge); border-radius: 10px; background: var(--c-page); }
.cmp-shell-us { border-color: var(--c-ink3); }
.cmp-table thead th { border-bottom: 1px solid var(--c-edge); background: var(--c-canvas); color: var(--c-ink3); font-weight: 600; letter-spacing: 0; text-transform: none; font-size: 0.8rem; }
.cmp-table thead .cmp-th-us { color: var(--c-ink); }
.cmp-table tbody tr + tr, .cmp-table tbody tr + tr th, .cmp-table tbody tr + tr td { border-top-color: var(--c-edge); }
.cmp-table tbody th { color: var(--c-ink); font-weight: 650; }
.cmp-table tbody td { color: var(--c-ink2); }
.cmp-table tbody .cmp-td-us { background: var(--c-us); color: var(--c-ink); font-weight: 550; }
.cmp-m-label { color: var(--c-ink3); font-weight: 600; letter-spacing: 0; text-transform: none; font-size: 0.75rem; }
.cmp-who-card, .cmp-why-card, .cmp-decision, .cmp-steps li, .cmp-shot { border-color: var(--c-edge); border-radius: 10px; background: var(--c-page); }
.cmp-who-us, .cmp-decision-us { border-color: var(--c-ink3); background: var(--c-us); }
.cmp-who-label { color: var(--c-ink3); letter-spacing: 0; text-transform: none; font-weight: 600; font-size: 0.8rem; }
.cmp-who-us .cmp-who-label { color: var(--c-ink); }
.cmp-who-copy, .cmp-why-card h3 { color: var(--c-ink); }
.cmp-who-meta a, .cmp-why-more a, .cmp-row-link, .cmp-step-link, .cmp-note a { color: var(--c-ink); text-decoration: underline; text-underline-offset: 2px; }
.cmp-mobile-quote { border-left-color: var(--c-edge); }
.cmp-final-panel { border-radius: 10px; }
.dark .cmp .cmp-final-panel { border-color: var(--c-edge); background: var(--c-canvas); }
.dark .cmp .cmp-final-panel > p, .dark .cmp .cmp-final-panel .trial-disclosure { color: var(--c-ink2); }
.dark .cmp .cmp-final-logo { color: var(--c-ink); }
.cmp-step-num { color: var(--c-ink3); }
.dark .cmp-faq dd, .dark .cmp-note, .dark .cmp-why-more { color: var(--c-ink2); }

/* Dark mode for the sections below the hero: the original CSS hard-codes light greys, so re-tone by role.
   :where() keeps the specificity low so the specific rules above (buttons, final panel) still win. */
.dark .cmp :where(p, li, dd, td, span, figcaption, footer) { color: var(--c-ink2); }
.dark .cmp :where(h1, h2, h3, dt, summary, strong, th) { color: var(--c-ink); }
.dark .cmp .cmp-answer p:not(.cmp-answer-label) { color: var(--c-ink); }
.dark .cmp .cmp-answer-label, .dark .cmp .cmp-head p { color: #adadad; }
.dark .cmp .cmp-head p, .dark .cmp .cmp-who-label, .dark .cmp .cmp-m-label { color: var(--c-ink3); }
.dark .cmp .cmp-math, .dark .cmp .cmp-method { border-color: var(--c-edge); background: var(--c-canvas); color: var(--c-ink2); }
.dark .cmp .cmp-math { color: var(--c-ink); }
.dark .cmp .cmp-team strong, .dark .cmp .cmp-team-buys { color: var(--c-ink); }
.dark .cmp .cmp-team-foot { color: var(--c-ink3); }
/* Cta03's buttons carry their own ink; without the :not() its dark primary button got white text on white. */
.dark .cmp :where(a):not(.cmp-btn):not(.cmp-sticky-btn):not(.cta-btn) { color: var(--c-ink); }
.dark .cmp .cmp-btn { color: var(--c-on-primary); }
/* MobileAppProof keeps a light App Store card, which stays white here while the dark rules above light its text. */
.dark .cmp :deep(.map-store) { border-color: var(--c-edge); background: var(--c-canvas); }
.dark .cmp :deep(.map-store-title) { color: var(--c-ink3); }
.dark .cmp :deep(.map-store dd) { color: var(--c-ink2); }
.dark .cmp .cmp-why-src, .dark .cmp .cmp-src { color: var(--c-ink3); }
.dark .cmp .cmp-why-label { color: var(--c-ink); }
.dark .cmp .cmp-shot img { background: var(--c-canvas); }
.dark .cmp .cmp-sticky { border-top-color: var(--c-edge); background: rgb(18 18 18 / 0.96); }
.dark .cmp .cmp-sticky-btn { background: var(--c-primary); color: var(--c-on-primary); }
.cmp-sticky { font-size: 1rem; }
.cmp-sticky > div { min-width: 0; flex: 1; }
/* Below 480px the short trial line beside the button ran 4-7 lines at 125% text. Stack it: the button
   full width, the trial line directly under it on one full-width row (B1.1), and room for the taller bar. */
@media (max-width: 479px) {
  .cmp-sticky { flex-wrap: wrap; gap: 0.45rem; }
  .cmp-sticky > div { display: contents; }
  .cmp-sticky-btn { order: 1; flex: 1 1 100%; }
  .cmp-sticky :deep(.trial-disclosure.compact) { order: 2; flex: 1 1 100%; text-align: center; }
  .cmp { padding-bottom: 7.5rem; }
}
.cmp-sticky-btn { border-radius: 6px; background: var(--c-primary); color: var(--c-on-primary); }
/* Pricing beside the trial button, as in PhoneStickyCta (Q088 board, 2026-11-01). Both 44px+ tall. */
.cmp-sticky-actions { display: flex; flex-shrink: 0; gap: 0.5rem; }
.cmp-sticky-btn { min-height: 2.75rem; }
.cmp-sticky-pricing { display: inline-flex; align-items: center; justify-content: center; min-height: 2.75rem; border: 1px solid var(--c-edge); border-radius: 6px; color: var(--c-ink); padding: 0.5rem 1rem; font-size: 0.84rem; font-weight: 700; text-align: center; line-height: 1.15; }
.dark .cmp .cmp-sticky-pricing { color: var(--c-ink); }
@media (max-width: 479px) {
  .cmp-sticky > .cmp-sticky-actions { order: 1; flex: 1 1 100%; display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 0.6rem; }
  .cmp-sticky-btn, .cmp-sticky-pricing { min-height: 2.875rem; }
}
</style>
